import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
    MappedComponentProperties,
} from '@adobe/aem-react-editable-components';

import './Level3Questions.css';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import SessionStorage from '../../utils/SessionStorage';
import { cleanInternalLinkUrl, wrapTrademarkSuperscript } from '../../utils/cob-utils';

import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import {
    AcquisitionFlow,
    clearSessionAndRedirectToDataEntryPage,
    clearSessionStorageVariablesForAcquisitionFlow,
    getAcquisitionFlow,
    getAppData,
    getDataEntryPages,
    submitConsent,
} from '../../utils/acquisition-utils';

import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

import { ApplicationApprovedResponse } from '../../types/responses/ApplicationApprovedResponse';
import { ApplicationPendingResponse } from '../../types/responses/ApplicationPendingResponse';
import { GetApplicationAuthorizedResponse } from '../../types/responses/GetApplicationAuthorizedResponse';
import { AcquisitionsLoader } from '../AcquisitionsLoader/AcquisitionsLoader';
import { callAcquisitionBff } from 'utils/acquisition-api/AcquisitionBff';
import { InputRadio } from 'component-library/index';
import { Button, Row, Col, Container } from '../../importCL';

export interface ILevel3QuestionsProps extends MappedComponentProperties {
    author?:boolean;
    header?: string;
    declinedPathURL?: string;
    applicationApprovedUrl?: string;
    customerDocumentationVerifyPageUrl?: string;
    oneTimePasscodePageUrl?: string;
    approvedMobileWalletUrl?: string;
    dataEntryUrl?: string;
    pendingUrl?: string;
    approvedPendingFundingUrl?: string;

    generalErrorCode?: string;
    apiErrorCode?: string;
    invalidErrorCode?: string;
}

export const Level3QuestionsEditConfig = {
    emptyLabel: 'Level 3 Questions',
    isEmpty: (props: PropsWithChildren<ILevel3QuestionsProps>) => {
        return !props || !props.header;
    },
};

export interface ILevel3QuestionsApiResponse {
    IsError: boolean;
    ErrorMessage: string;
    NotAnsweredQuestions: number[];
    SubmitApplicationEnum?: number;
    QuestionsRequest?: {
        QuestionText: string;
        Answers: { AnswerText: string }[];
        AnswerSelected: number;
    }[];
    AdditionalInformation?: { [key: string]: any };
}

interface Level3QuestionProps {
    QuestionIndex: number;
    QuestionText: string;
    Answers: { AnswerText: string }[];
    AnswerSelected: number;
}

interface AnswerSelection {
    questionIndex: number;
    answerIndex: number;
    question: string;
    value: string;
}

const redirectToUrl = (
    url: string,
    options?: { clearSession?: boolean; timeout?: NodeJS.Timeout | null },
) => {
    if (options) {
        if (options.clearSession) {
            clearSessionStorageVariablesForAcquisitionFlow();
        }
        if (options.timeout) {
            clearTimeout(options.timeout);
        }
    }
    window.location.href = cleanInternalLinkUrl(url + window.location.search);
};

const Level3Questions = (
    props: PropsWithChildren<ILevel3QuestionsProps>,
): JSX.Element => {
    const { showPushdown, closePushdown } = usePushdownContext();

    const [data, setData] = useState<ILevel3QuestionsApiResponse>();
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const loaderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const answerUnset = -1;
    const answerError = -2;
    const [answersArray, setAnswersArray] = useState<AnswerSelection[]>([]);
    const addAnswer = (
        QuestionIndex: number,
        AnswerIndex: number,
        Question: string,
        Answer: string,
    ) => {
        setAnswersArray(curr => {
            return [
                ...curr,
                {
                    questionIndex: QuestionIndex,
                    answerIndex: AnswerIndex,
                    question: Question,
                    value: Answer,
                },
            ];
        });
    };
    const findAnswer = (QuestionIndex: number): boolean => {
        for (const element of answersArray) {
            if (QuestionIndex === element.questionIndex) {
                return true;
            }
        }
        return false;
    };
    const updateAnswer = (
        QuestionIndex: number,
        AnswerIndex: number,
        _Question: string,
        Answer: string,
    ) => {
        const updatedAnswers = [...answersArray];
        for (const answer of updatedAnswers) {
            if (answer.questionIndex === QuestionIndex) {
                answer.answerIndex = AnswerIndex + 1;
                answer.value = Answer;
            }
        }
        setAnswersArray(updatedAnswers);
    };
    const getAnswerForQuestion = (QuestionIndex: number): string => {
        for (const answer of answersArray) {
            if (answer.questionIndex === QuestionIndex) {
                return answer.value;
            }
        }
        return '';
    };
    const getAnswerIndexForQuestion = (QuestionIndex: number): number => {
        for (const answer of answersArray) {
            if (answer.questionIndex === QuestionIndex) {
                return answer.answerIndex;
            }
        }
        return answerError;
    };

    const issuePushdown = (e: string) => {
        if (loaderTimeoutRef.current) {
            clearTimeout(loaderTimeoutRef.current);
            loaderTimeoutRef.current = null;
        }
        setShowLoader(false);

        showPushdown(e);
    };

    const getQuestionsFromSession = () => {
        const applicationResponse = SessionStorage.getObject('flowApplicationData');
        const questions = applicationResponse?.stepUpAuthorization?.kba?.questionSet;

        if (!questions) {
            redirectToUrl(props.dataEntryUrl!, {
                clearSession: true,
                timeout: loaderTimeoutRef.current,
            });

            return null;
        }

        const formattedQuestions: ILevel3QuestionsApiResponse = {
            QuestionsRequest: questions.map(question => {
                return {
                    QuestionText: question.questionText,
                    QuestionType: question.questionType,
                    Answers: question.questionSelect.map(answer => {
                        return {
                            AnswerText: answer.choiceText,
                            AnswerId: answer.choiceId,
                        };
                    }),
                    AnswerSelected: -1,
                };
            }),
            IsError: false,
            ErrorMessage: '',
            NotAnsweredQuestions: [],
        };

        return formattedQuestions;
    };

    useEffect(() => {
        setShowLoader(true);
        if (getAcquisitionFlow() === AcquisitionFlow.PHA && !props.author) {
            const questions = getQuestionsFromSession();
            setData(questions);
            setShowLoader(false);
            return;
        }

        if (SessionStorage.verifyNotEmpty('initResponse')) {
            const ApplicationID = SessionStorage.tryGet('ApplicationId', null);
            if (ApplicationID) {
                acquisitionApiRequest<ILevel3QuestionsApiResponse>(
                    'get-questions',
                    {
                        body: {
                            ApplicationId: ApplicationID,
                        },
                    },
                ).then(response => {
                    setData(response.data);
                    setShowLoader(false);
                })
                .catch(response => {
                    if (response.status !== 200) {
                        if(getAcquisitionFlow() === AcquisitionFlow.PqOfOne) {
                            clearSessionAndRedirectToDataEntryPage('pqOfOne', getDataEntryPages().preQualDataEntryPageUrl); 
                        } else {
                            clearSessionAndRedirectToDataEntryPage('processingError'); 
                        }
                    }
                });
            }
        }
    }, []);

    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [data]);

    const Level3Question = (props: Level3QuestionProps) => {
        if (!findAnswer(props.QuestionIndex)) {
            addAnswer(props.QuestionIndex, -1, props.QuestionText, '');
        }
        return (
            <Row className='LevelThreeQuestions__questionRow'>
                <div className='LevelThreeQuestions__questionPrompt'>
                    {'' + props.QuestionIndex + '. ' + props.QuestionText}
                </div>
                {props.Answers.map((option, index) => {
                    return (
                        <InputRadio
                            className='LevelThreeQuestions__questionOption'
                            id={
                                'optionID-group-' +
                                props.QuestionIndex +
                                '-' +
                                index
                            }
                            setValue={value => {
                                updateAnswer(
                                    props.QuestionIndex,
                                    index,
                                    props.QuestionText,
                                    value,
                                );
                            }}
                            name={'question-group-' + props.QuestionIndex}
                            value={option.AnswerText}
                            label={option.AnswerText}
                            key={
                                'key-group-' + props.QuestionIndex + '-' + index
                            }
                            checked={
                                getAnswerForQuestion(props.QuestionIndex) ===
                                option.AnswerText
                            }
                            error={
                                getAnswerIndexForQuestion(
                                    props.QuestionIndex,
                                ) === answerError
                            }
                        />
                    );
                })}
            </Row>
        );
    };

    const submitAnswers = () => {
        closePushdown();
        setShowLoader(true);
        loaderTimeoutRef.current = setTimeout(() => {
            setShowLoader(false);
        }, 90000);
        let areAnswersValid = true;
        for (const answer of answersArray) {
            if (
                answer.answerIndex === answerUnset ||
                answer.answerIndex === answerError
            ) {
                areAnswersValid = false;
                answer.answerIndex = answerError;
            }
        }
        if (!areAnswersValid) {
            issuePushdown(props.invalidErrorCode!);
            return;
        }

        const selectedAnswers = answersArray.map(element => {
            return {
                QuestionText: element.question,
                Answers: [{ AnswerText: element.value }],
                AnswerSelected: element.answerIndex,
            };
        });
        const applicationID = SessionStorage.get('ApplicationId');

        // if pha-secured flow, use new API
        if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
            callAcquisitionBff('stepup-authorization', {
                replace: { 'applicationId': applicationID },
                body: {
                    application: {
                        stepUpType: 'KIQ',
                        questionAnswerId: selectedAnswers.map((answer, index) => {

                            return {
                                questionType: typeof answer.QuestionText,
                                questionOrderId: index,
                                questionChoiceId: answer.AnswerSelected - 1,
                            };
                        }),
                    },
                },
            }).then(async (response: any) => {
                const flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', {});
                SessionStorage.setObject('flowApplicationData', {
                    ...flowApplicationData,
                    ...response.data,
                });

                const status = response.data.applicationStatus.applicationStatus.toUpperCase();
                switch (status) {
                    case 'APPROVED': {
                        const consent = await submitConsent(flowApplicationData);
                        if (consent) {
                            redirectToUrl(props.applicationApprovedUrl!, {
                                timeout: loaderTimeoutRef.current,
                            });
                        } else {
                            clearSessionAndRedirectToDataEntryPage(props.generalErrorCode!);
                        }
                        break;
                    }
                    case 'DECLINED':
                    case 'SOFTDECLINE':
                    case 'HARDDECLINE': {
                        const consent = await submitConsent(flowApplicationData);
                        if (consent) {
                            redirectToUrl(props.declinedPathURL!, {
                                timeout: loaderTimeoutRef.current,
                            });
                        } else {
                            clearSessionAndRedirectToDataEntryPage(props.generalErrorCode!);
                        }
                        break;
                    }
                    case 'PENDING':
                    case 'QUEUED': {
                        const consent = await submitConsent(flowApplicationData);
                        if (consent) {
                            redirectToUrl(props.pendingUrl!, {
                                timeout: loaderTimeoutRef.current,
                            });
                        }
                        clearSessionAndRedirectToDataEntryPage(props.generalErrorCode!);
                        break;
                    }
                    default:
                        loaderTimeoutRef?.current && clearTimeout(loaderTimeoutRef.current);
                        clearSessionAndRedirectToDataEntryPage();
                        break;
                }
            }).catch(() => {
                issuePushdown(props.generalErrorCode!);
            });

            return;
        }

        acquisitionApiRequest<ILevel3QuestionsApiResponse>('submit-questions', {
            body: {
                QuestionsRequest: selectedAnswers,
                ApplicationId: applicationID,
            },
        })
            .then(async (response) => {
                await SessionStorage.setCipher('levelThreeResponse', response);
                const flow = getAcquisitionFlow();
                const approvedApiNames: Record<AcquisitionFlow, string> = {
                    [AcquisitionFlow.PQ]: 'pq-app-approved',
                    [AcquisitionFlow.PA]: 'pa-app-approved',
                    [AcquisitionFlow.SkipPQ]: 'skip-pq-app-approved',
                    [AcquisitionFlow.PqOfOne]: 'skip-pq-app-approved',
                    [AcquisitionFlow.PHA]: '',
                    [AcquisitionFlow.SkipPQCounter]: undefined,
                };
                const pendingApiNames: Record<AcquisitionFlow, string> = {
                    [AcquisitionFlow.PQ]: 'pq-app-pending',
                    [AcquisitionFlow.PA]: 'pa-app-pending',
                    [AcquisitionFlow.SkipPQ]: 'skip-pq-app-pending',
                    [AcquisitionFlow.PqOfOne]: 'skip-pq-app-pending', 
                    [AcquisitionFlow.PHA]: '', 
                    [AcquisitionFlow.SkipPQCounter]: undefined,
                };
                switch (response.data.SubmitApplicationEnum) {
                    case 1: // DeclinedWithESignCredit
                    case 2: // DeclinedWithESignCreditNoScore
                    case 3: // DeclinedWithESignNonCredit
                    case 4: // DeclinedWithEsignNCDLetter // Mentioned in Legacy but not in the API Docs? //https://gitlab.com/creditonebank/marketing/web-engineering/web-apps/credit-one/-/blob/develop/packages/application/src/main/content/jcr_root/etc/clientlibs/creditonebank/clientlibs-components/level-3-questions/js/level-3-questions.js#L182 and https://test03aem.creditone.com/acquisitionapi/docs/output/AcquisitionApi.html#shared-v1-submit-level-3-questions-v1-post
                    case 6: // DeclinedWithESignOfad
                        redirectToUrl(props.declinedPathURL!, {
                            timeout: loaderTimeoutRef.current,
                        });
                        break;
                    case 7: // Approved
                        acquisitionApiRequest<ApplicationApprovedResponse>(
                            approvedApiNames[flow],
                            {
                                body: {},
                                method: 'GET',
                            },
                        )
                            .then(async (approvedResponse) => {
                                await SessionStorage.setCipherWithCurrentScope(
                                    'app-approved-data',
                                    approvedResponse.data,
                                );
                                
                                const appData = await getAppData();

                                if (appData?.IsSecuredCard) {
                                    redirectToUrl(props.approvedPendingFundingUrl!, {
                                        timeout: loaderTimeoutRef.current,
                                    });
                                    return;
                                }

                                switch (approvedResponse.data.InstantIssuanceOffer) {
                                    case 0: // None
                                        redirectToUrl(
                                            props.applicationApprovedUrl!,
                                            {
                                                timeout:
                                                    loaderTimeoutRef.current,
                                            },
                                        );
                                        break;
                                    case 2: // MobileWallet
                                        redirectToUrl(
                                            props.approvedMobileWalletUrl!,
                                            {
                                                timeout:
                                                    loaderTimeoutRef.current,
                                            },
                                        );
                                        break;
                                    default:
                                        clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryUrl, 'Default instant issuance offer error');
                                        SessionStorage.remove(
                                            'app-approved-data',
                                        );
                                        break;
                                }
                            })
                            .catch(() => {
                                issuePushdown(props.apiErrorCode!);
                            });
                        break;
                    case 8: // Pending
                        acquisitionApiRequest<ApplicationPendingResponse>(
                            pendingApiNames[flow],
                            {
                                body: {},
                                method: 'GET',
                            },
                        )
                            .then(async (response) => {
                                await SessionStorage.setCipherWithCurrentScope(
                                    'app-pending-data',
                                    response.data,
                                );
                                redirectToUrl(props.pendingUrl!, {
                                    timeout: loaderTimeoutRef.current,
                                });
                            })
                            .catch(() => {
                                issuePushdown(props.apiErrorCode!);
                            });
                        break;
                    case 9: // Authorize
                        acquisitionApiRequest<GetApplicationAuthorizedResponse>(
                            'get-application-authorized',
                            {
                                body: {},
                                method: 'GET',
                            },
                        )
                            .then(async (response) => {
                                await SessionStorage.setCipherWithCurrentScope(
                                    'appAuthorizedResponse',
                                    response.data,
                                );
                                switch (parseInt(response.data.NextAction)) {
                                    case 0: // No Action
                                        clearSessionAndRedirectToDataEntryPage('appInProgress');
                                        break;
                                    case 1: // KIQQuestions
                                        window.location.reload();
                                        break;
                                    case 2: // OneTimePasscode
                                        redirectToUrl(
                                            props.oneTimePasscodePageUrl!,
                                            {
                                                timeout:
                                                    loaderTimeoutRef.current,
                                            },
                                        );
                                        break;
                                    case 3: // CustomerVerificationNoFaceBiometric
                                    case 4: // CustomerVerificationWithfaceBiometric
                                        redirectToUrl(
                                            props.customerDocumentationVerifyPageUrl!,
                                            {
                                                timeout:
                                                    loaderTimeoutRef.current,
                                            },
                                        );
                                        break;
                                    default: // Error? // Copied structure from Legacy
                                        SessionStorage.remove(
                                            'appAuthorizedResponse',
                                        );
                                        clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Default application authorization error');
                                        break;
                                }
                            })
                            .catch(() => {
                                issuePushdown(props.apiErrorCode!);
                            });
                        break;
                    case 10: // Cancel
                    case 11: // Error
                        redirectToUrl(props.dataEntryUrl!, {
                            clearSession: true,
                            timeout: loaderTimeoutRef.current,
                        });
                        break;
                    case 12: // In Process
                    case 13: // PendingSSN
                    case 14: // Queued
                        redirectToUrl(props.pendingUrl!, {
                            timeout: loaderTimeoutRef.current,
                        });
                        break;
                    default:
                        loaderTimeoutRef?.current && clearTimeout(loaderTimeoutRef.current);
                        clearSessionAndRedirectToDataEntryPage();
                        break;
                }
            })
            .catch(rejection => {
                if (rejection && rejection.status === 408) {
                    redirectToUrl(props.dataEntryUrl!, {
                        clearSession: true,
                        timeout: loaderTimeoutRef.current,
                    });
                }
                issuePushdown(props.generalErrorCode!);
            });
    };

    if (showLoader) {
        return (
            <AcquisitionsLoader
                processingSteps={['Validating personal information']}
            />
        );
    }

    return (
        <Container id='level-three-questions' className='LevelThreeQuestions'>
            {props.header && (
                <Row>
                    <Col>
                        <h2 className='LevelThreeQuestions__header'>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.header))}
                        </h2>
                    </Col>
                </Row>
            )}
            <Row className='LevelThreeQuestions__questionsListRow'>
                <Col>
                    {data &&
                        data.QuestionsRequest!.map((questionRequest, index) => {
                            return (
                                <Level3Question
                                    QuestionIndex={index + 1}
                                    {...questionRequest}
                                    key={'group-' + index}
                                />
                            );
                        })}
                </Col>
            </Row>
            <Row className='LevelThreeQuestions__buttonRow'>
                <Col>
                    <div className='LevelThreeQuestions__buttons'>
                        <Button
                            size='medium'
                            className='LevelThreeQuestions__submitButton'
                            onClick={submitAnswers}
                        >
                            Submit
                        </Button>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default Level3Questions;